import type { SupabaseClient } from '@supabase/supabase-js';
import { queryOptions } from '@tanstack/react-query';

import {
  formatInTenantTz,
  resolveTimezone,
  tenantCalendarDayKey,
} from '@kit/shared/timezone';

import type { Database } from '~/lib/database.types';

export type LandingScheduleSession =
  Database['public']['Functions']['get_available_classes_for_date']['Returns'][number];

export function landingScheduleWindow(now: Date, daysAhead: number, timeZone: string) {
  const today = tenantCalendarDayKey(now, timeZone);
  const end = new Date(`${today}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + daysAhead);

  return {
    today,
    endDate: end.toISOString().slice(0, 10),
    currentTime: formatInTenantTz(now, 'HH:mm:ss', timeZone),
  };
}

export function normalizeLandingSchedule(
  sessions: LandingScheduleSession[],
  window: ReturnType<typeof landingScheduleWindow>,
) {
  return sessions
    .filter(
      (session) =>
        session.booking_date <= window.endDate &&
        (session.booking_date > window.today ||
          (session.booking_date === window.today &&
            session.start_time > window.currentTime)),
    )
    .sort(
      (a, b) =>
        a.booking_date.localeCompare(b.booking_date) ||
        a.start_time.localeCompare(b.start_time) ||
        a.booking_id.localeCompare(b.booking_id),
    );
}

export function landingScheduleQueryOptions(
  client: SupabaseClient<Database>,
  accountSlug: string | undefined,
  daysAhead = 7,
  timeZone?: string,
  category?: 'class' | 'event',
) {
  const tenantTimeZone = resolveTimezone(timeZone);

  return queryOptions({
    queryKey: ['landing-schedule', accountSlug, daysAhead, tenantTimeZone, ...(category ? [category] : [])],
    queryFn: async () => {
      if (!accountSlug) return [];

      const window = landingScheduleWindow(new Date(), daysAhead, tenantTimeZone);
      const { data, error } = await client.rpc('get_available_classes_for_date', {
        p_account_slug: accountSlug,
        p_date: window.today,
        p_end_date: window.endDate,
        ...(category ? { p_category: category } : {}),
      });

      if (error) throw error;

      return normalizeLandingSchedule(data ?? [], window);
    },
    enabled: Boolean(accountSlug),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}
